import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Shield, Ban, Play, Eye, Download, CheckCircle, AlertCircle, FileText, Clock, X, Upload } from 'lucide-react';
import { downloadFile } from '../utils/fileUtils';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedPr, setSelectedPr] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(''); // 'hold' or 'unhold'
  const [holdReason, setHoldReason] = useState('');
  const [holdReasonType, setHoldReasonType] = useState('');
  const [loading, setLoading] = useState(false);
  const [vendorDetails, setVendorDetails] = useState(null);
  
  // Pagination & Filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const getStatusLabel = (status) => {
    const statusMap = {
      'pending_approval': 'Pending Function Head Approval',
      'approved': 'PO Issued To Vendor',
      'rejected': 'Rejected By Function Head',
      'tax_invoice_uploaded': 'Pending Finance Approval',
      'additional_info_requested': 'Addl Info Requested',
      'finance_approved': 'Approved For Payment',
      'paid': 'Paid'
    };
    return statusMap[status] || status.replace('_', ' ');
  };

  const getStatusBadge = (pr) => {
    const status = pr.status;
    const statusConfig = {
      'pending_approval': { label: 'Pending Function Head Approval', color: 'bg-yellow-100 text-yellow-800' },
      'approved': { label: 'PO Issued To Vendor', color: 'bg-green-100 text-green-800' },
      'rejected': { 
        label: pr.rejected_by && pr.rejected_by.includes('finance') ? 'Rejected By Finance Head' : 'Rejected By Function Head', 
        color: 'bg-red-100 text-red-800' 
      },
      'pending_finance_approval': { label: 'Pending Finance Approval', color: 'bg-cyan-100 text-cyan-800' },
      'tax_invoice_uploaded': { label: 'Pending Finance Approval', color: 'bg-blue-100 text-blue-800' },
      'additional_info_requested': { label: 'Addl Info Requested', color: 'bg-orange-100 text-orange-800' },
      'finance_approved': { label: 'Approved For Payment', color: 'bg-purple-100 text-purple-800' },
      'paid': { label: 'Paid', color: 'bg-emerald-100 text-emerald-800' }
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    
    // Return badges with request type indicator
    return (
      <div className="flex flex-col gap-1 items-start">
        {pr?.is_advance_payment && (
          <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs font-semibold">AR</span>
          </Badge>
        )}
        {!pr?.is_advance_payment && !pr?.is_upload_invoice && (
          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs font-semibold">PR</span>
          </Badge>
        )}
        {pr?.is_upload_invoice && (
          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <Upload className="w-3 h-3" />
            <span className="text-xs font-semibold">UI</span>
          </Badge>
        )}
        <Badge className={`${config.color} inline-flex w-auto`}>{config.label}</Badge>
      </div>
    );
  };

  const getVendorName = (vendorId) => {
    if (!vendorId) return 'N/A';
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor?.legal_entity_name || vendor?.trade_name || 'N/A';
  };

  const openViewDialog = async (pr) => {
    setSelectedPr(pr);
    setViewDialogOpen(true);
    
    // Reset vendor details to prevent stale data
    setVendorDetails(null);
    
    // Fetch vendor details if vendor_id exists
    if (pr.vendor_id) {
      try {
        const response = await axios.get(`${API}/vendors/${pr.vendor_id}`, {
          headers: getAuthHeader()
        });
        setVendorDetails(response.data);
      } catch (error) {
        console.error('Failed to fetch vendor details:', error);
        setVendorDetails(null);
      }
    }
  };

  useEffect(() => {
    fetchAllPayments();
    fetchVendors();
  }, []);

  // Helper function to preserve file extension from S3 key
  const getFilenameWithExtension = (s3Key, defaultName) => {
    if (!s3Key) return defaultName;
    
    // Extract original filename from S3 key (e.g., "proforma/uuid_invoice.png" -> "invoice.png")
    const parts = s3Key.split('/');
    const filename = parts[parts.length - 1];
    
    // Extract extension from original filename
    const extensionMatch = filename.match(/\.[^.]+$/);
    const extension = extensionMatch ? extensionMatch[0] : '.pdf';
    
    // Return default name with correct extension
    return defaultName.replace(/\.[^.]*$/, '') + extension;
  };

  // Helper function to get advance payment status badge
  const getAdvanceStatusBadge = (pr) => {
    if (!pr.is_advance_payment) return null;
    
    // Don't show badge if status is none or undefined
    if (!pr.advance_payment_status || pr.advance_payment_status === 'none') return null;
    
    const statusConfig = {
      'pending_ppa': { label: 'Pending PPA', color: 'bg-yellow-100 text-yellow-800' },
      'approved_pp': { label: 'Approved PP', color: 'bg-blue-100 text-blue-800' },
      'ppa_approved': { label: 'PP Approved', color: 'bg-green-100 text-green-800' },
      'ppa_rejected': { label: 'PP Rejected', color: 'bg-red-100 text-red-800' },
      'advance_paid': { label: 'Advance Paid', color: 'bg-emerald-100 text-emerald-800' },
      'paid': { label: 'Advance Paid', color: 'bg-emerald-100 text-emerald-800' }
    };
    
    const config = statusConfig[pr.advance_payment_status];
    if (!config) return null;
    
    return (
      <Badge className={`${config.color} text-xs inline-flex w-auto`}>
        {config.label}
      </Badge>
    );
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, {
        headers: getAuthHeader()
      });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      // Don't show error toast for vendors as it's not critical for admin view
    }
  };

  const fetchAllPayments = async () => {
    try {
      // Fetch ALL purchase requests for admin view (including rejected)
      const response = await axios.get(`${API}/purchase-requests`, { headers: getAuthHeader() });
      
      // Display all requests (including paid and rejected)
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to fetch purchase requests');
    }
  };

  const handleAction = async () => {
    if (actionType === 'hold' && !holdReason.trim()) {
      toast.error('Hold reason is required');
      return;
    }

    if (actionType === 'hold' && !holdReasonType) {
      toast.error('Hold reason type is required');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/purchase-requests/${selectedPr.id}/hold`,
        {
          action: actionType,
          reason: actionType === 'hold' ? holdReason : null,
          hold_reason_type: actionType === 'hold' ? holdReasonType : null
        },
        { headers: getAuthHeader() }
      );

      toast.success(
        actionType === 'hold'
          ? 'Request put on hold'
          : 'Request hold removed'
      );
      setDialogOpen(false);
      setHoldReason('');
      setHoldReasonType('');
      fetchAllPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (pr, action) => {
    setSelectedPr(pr);
    setActionType(action);
    setHoldReason('');
    setHoldReasonType('');
    setDialogOpen(true);
  };

  // Filter and paginate data
  const getFilteredPayments = () => {
    let filtered = [...payments];

    // Apply search filter (search by requested_by, vendor name, cost_centre)
    if (searchTerm) {
      filtered = filtered.filter(pr => 
        pr.requested_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getVendorName(pr.vendor_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pr.entity_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.cost_centre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.po_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'on_hold') {
        filtered = filtered.filter(pr => pr.hold_status);
      } else {
        filtered = filtered.filter(pr => pr.status === statusFilter);
      }
    }

    return filtered;
  };

  const filteredPayments = getFilteredPayments();
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPayments = filteredPayments.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-red-600" />
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
                Admin Controls
              </h1>
            </div>
          </div>
        </div>
      </nav>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card className="mb-6 border-0 shadow-lg bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800">Admin Override - Hold/Unhold Requests</CardTitle>
            <CardDescription className="text-red-700">
              You can hold or unhold purchase requests at ANY workflow stage. This action will notify relevant stakeholders.
            </CardDescription>
          </CardHeader>
        </Card>
        {payments.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No active requests</h3>
              <p className="text-gray-600">There are no active purchase requests to manage at this time</p>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="admin-request-list">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-900">
                Admin Controls - Purchase Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Filter and Search Controls */}
              <div className="mb-4 flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-3 items-center flex-1">
                  <input
                    type="text"
                    placeholder="Search by requester, vendor, entity, cost center, PO..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 flex-1 max-w-md"
                  />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="all">All Status</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">PO Issued</option>
                    <option value="po_generated">Pending Accounts Approval</option>
                    <option value="tax_invoice_uploaded">Pending Finance</option>
                    <option value="additional_info_requested">Info Requested</option>
                    <option value="finance_approved">Approved For Payment</option>
                    <option value="paid">Paid</option>
                    <option value="rejected">Rejected By Finance Head</option>
                    <option value="on_hold">On Hold</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-600">per page</span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Requested By</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Approved By</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Vendor</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Entity</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Cost Center</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                      <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">View</th>
                      <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Hold</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentPayments.map((pr) => (
                      <tr key={pr.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {pr.requested_by}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {pr.approved_by || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {getVendorName(pr.vendor_id)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {pr.entity_name || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {pr.cost_centre}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          ₹{(pr.base_amount || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(pr)}
                          {getAdvanceStatusBadge(pr) && (
                            <div className="mt-1">{getAdvanceStatusBadge(pr)}</div>
                          )}
                          {pr.hold_status && (
                            <div className="mt-1">
                              <Badge className="bg-red-100 text-red-800 w-auto inline-flex">On Hold</Badge>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <Button
                            onClick={() => openViewDialog(pr)}
                            variant="ghost"
                            size="sm"
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {pr.status === 'paid' || (pr.status === 'rejected' && pr.rejected_by && pr.rejected_by.includes('finance')) ? (
                            <Button
                              size="sm"
                              disabled
                              className="bg-gray-300 text-gray-500 cursor-not-allowed"
                              title={pr.status === 'paid' ? "Cannot hold/unhold paid requests" : "Cannot hold/unhold rejected requests"}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          ) : pr.hold_status ? (
                            <Button
                              onClick={() => openActionDialog(pr, 'unhold')}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`unhold-button-${pr.id}`}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              onClick={() => openActionDialog(pr, 'hold')}
                              size="sm"
                              variant="destructive"
                              data-testid={`hold-button-${pr.id}`}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {filteredPayments.length > 0 && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredPayments.length)} of {filteredPayments.length} results
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="sm"
                    >
                      Previous
                    </Button>
                    <div className="flex gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          // Show first page, last page, current page, and pages around current
                          return page === 1 || 
                                 page === totalPages || 
                                 (page >= currentPage - 1 && page <= currentPage + 1);
                        })
                        .map((page, index, array) => (
                          <div key={page} className="flex items-center">
                            {index > 0 && array[index - 1] !== page - 1 && (
                              <span className="px-2 text-gray-400">...</span>
                            )}
                            <Button
                              onClick={() => setCurrentPage(page)}
                              variant={currentPage === page ? "default" : "outline"}
                              size="sm"
                              className={currentPage === page ? "bg-red-600 hover:bg-red-700" : ""}
                            >
                              {page}
                            </Button>
                          </div>
                        ))
                      }
                    </div>
                    <Button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      variant="outline"
                      size="sm"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === 'hold' ? 'Put Request on Hold' : 'Remove Request Hold'}
              </DialogTitle>
              <DialogDescription>
                {actionType === 'hold'
                  ? 'This will pause the request workflow and notify relevant stakeholders.'
                  : 'This will resume the request workflow.'}
              </DialogDescription>
            </DialogHeader>

            {actionType === 'hold' && (
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="hold_reason_type">Hold Reason Type <span className="text-red-500">*</span></Label>
                  <select
                    id="hold_reason_type"
                    data-testid="hold-reason-type-select"
                    value={holdReasonType}
                    onChange={(e) => setHoldReasonType(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">Select Hold Reason Type</option>
                    <option value="Management Hold">Management Hold</option>
                    <option value="Accounts Query">Accounts Query</option>
                    <option value="Requester Query">Requester Query</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="hold_reason">Hold Reason <span className="text-red-500">*</span></Label>
                  <textarea
                    id="hold_reason"
                    data-testid="hold-reason-input"
                    value={holdReason}
                    onChange={(e) => setHoldReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows={4}
                    placeholder="Enter reason for holding this request..."
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAction}
                className={actionType === 'hold' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                disabled={loading}
                data-testid="confirm-admin-action-button"
              >
                {loading ? 'Processing...' : (actionType === 'hold' ? 'Put on Hold' : 'Remove Hold')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Purchase Request Details</DialogTitle>
              <DialogDescription>
                Complete view of the purchase request including invoice details and documents
              </DialogDescription>
            </DialogHeader>
            {selectedPr && (
              <div className="space-y-6">
                {/* Status Badges */}
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedPr)}
                  {selectedPr.hold_status && (
                    <Badge className="bg-red-100 text-red-800">On Hold</Badge>
                  )}
                </div>

                {/* Hold Reason if present */}
                {selectedPr.hold_status && selectedPr.hold_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-800 mb-2">Hold Reason:</p>
                    {selectedPr.hold_reason_type && (
                      <p className="text-sm font-medium text-red-800 mb-2">Type: {selectedPr.hold_reason_type}</p>
                    )}
                    <p className="text-sm text-red-700">{selectedPr.hold_reason}</p>
                    {selectedPr.held_by && selectedPr.held_at && (
                      <p className="text-xs text-red-600 mt-2">
                        Held by: {selectedPr.held_by} on {new Date(selectedPr.held_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Rejected Reason if present */}
                {selectedPr.status === 'rejected' && selectedPr.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-800 mb-2">Rejection Reason:</p>
                    <p className="text-sm text-red-700">{selectedPr.rejection_reason}</p>
                    {selectedPr.rejected_by && selectedPr.rejected_at && (
                      <p className="text-xs text-red-600 mt-2">
                        Rejected by: {selectedPr.rejected_by} on {new Date(selectedPr.rejected_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Requested By</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.requested_by}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Entity</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.entity_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Cost Centre</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.cost_centre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Base Amount</p>
                    <p className="text-sm font-medium mt-1">₹{(selectedPr.base_amount || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Inclusive Tax</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.inclusive_tax || 'No'}</p>
                  </div>
                  {selectedPr.inclusive_tax === 'Yes' && selectedPr.tax_amount > 0 && (
                    <div>
                      <p className="text-sm text-gray-600">Tax Amount</p>
                      <p className="text-sm font-medium mt-1">₹{(selectedPr.tax_amount || 0).toLocaleString()}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="text-sm font-medium mt-1 text-green-700">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Due Date</p>
                    <p className="text-sm font-medium mt-1">
                      {selectedPr.due_date ? new Date(selectedPr.due_date).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">PO Number</p>
                    <p className="text-sm font-medium mt-1">{selectedPr.po_number || 'Not Generated'}</p>
                  </div>
                  {selectedPr.finance_approved_at && (
                    <div>
                      <p className="text-sm text-gray-600">Finance Approved Date</p>
                      <p className="text-sm font-medium mt-1">
                        {new Date(selectedPr.finance_approved_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {selectedPr.scheduled_payment_days && selectedPr.scheduled_payment_date && (
                    <div>
                      <p className="text-sm text-gray-600">Schedule Payment After</p>
                      <p className="text-sm font-medium mt-1">
                        {selectedPr.scheduled_payment_days} days : {new Date(selectedPr.scheduled_payment_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {/* TDS Related Fields - Inline with other details */}
                  {selectedPr.is_tds_applicable === 'Yes' && (
                    <>
                      <div>
                        <p className="text-sm text-gray-600">TDS Applicable</p>
                        <p className="text-sm font-medium mt-1 text-orange-600">Yes</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">TDS Percentage</p>
                        <p className="text-sm font-medium mt-1">{selectedPr.tds_percentage || 0}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Payable Amount</p>
                        <p className="text-sm font-medium mt-1 text-blue-700 font-semibold">
                          ₹{(selectedPr.payable_amount || 0).toLocaleString()}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Reason */}
                <div>
                  <p className="text-sm text-gray-600 mb-2">Reason</p>
                  <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg">{selectedPr.reason || 'No reason provided'}</p>
                </div>

                {/* Vendor Information */}
                {vendorDetails && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Vendor</p>
                    <p className="text-sm font-medium text-gray-900 p-3 bg-gray-50 rounded-lg">
                      {vendorDetails.legal_entity_name}
                    </p>
                  </div>
                )}

                {/* Advance Payment Details */}
                {selectedPr.is_advance_payment && (
                  <div className="bg-amber-50 rounded-lg p-4 space-y-3 border-2 border-amber-200">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-amber-900">Advance Payment Details</h3>
                      {getAdvanceStatusBadge(selectedPr)}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium text-amber-700">Amount:</span>
                        <p className="text-sm font-bold text-amber-900">₹{(selectedPr.base_amount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-amber-700">Advance Payment:</span>
                        <p className="text-sm font-bold text-green-900 bg-green-50 p-2 rounded">₹{(selectedPr.advance_amount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-amber-700">Payment Status:</span>
                        <p className="text-sm font-medium text-amber-900">
                          {selectedPr.is_partial_paid ? 'Advance Paid' : 'Advance Pending'}
                        </p>
                      </div>
                      {selectedPr.advance_approved_by && (
                        <div className="col-span-2">
                          <span className="text-sm font-medium text-green-700">PPA Approved:</span>
                          <p className="text-sm text-green-900">By {selectedPr.advance_approved_by} on {new Date(selectedPr.advance_approved_at).toLocaleString()}</p>
                        </div>
                      )}
                      {selectedPr.advance_rejected_by && (
                        <>
                          <div className="col-span-2">
                            <span className="text-sm font-medium text-red-700">PPA Rejected:</span>
                            <p className="text-sm text-red-900">By {selectedPr.advance_rejected_by} on {new Date(selectedPr.advance_rejected_at).toLocaleString()}</p>
                          </div>
                          <div className="col-span-2 bg-red-50 p-2 rounded">
                            <span className="text-sm font-medium text-red-700">Rejection Reason:</span>
                            <p className="text-sm text-red-900">{selectedPr.advance_rejection_reason}</p>
                          </div>
                        </>
                      )}
                      {selectedPr.advance_utr_number && (
                        <div className="col-span-2">
                          <span className="text-sm font-medium text-green-700">Advance Payment UTR:</span>
                          <p className="text-sm text-green-900">{selectedPr.advance_utr_number} (Paid on {new Date(selectedPr.advance_paid_at).toLocaleDateString()})</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Partial Payment Info Section - Show if any partial payments made */}
                {((selectedPr.partial_payments && selectedPr.partial_payments.length > 0) || (selectedPr.total_paid_amount && selectedPr.total_paid_amount > 0)) && (
                  <div className="bg-amber-50 rounded-lg p-4 space-y-3 border-2 border-amber-200">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-amber-900">Partial Payment Info</h3>
                      <Badge className={selectedPr.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                        {selectedPr.payment_status === 'paid' ? 'Fully Paid' : 'Partly Paid'}
                      </Badge>
                    </div>
                    
                    {/* Balance Amount Summary */}
                    <div className="grid grid-cols-3 gap-4 bg-white p-3 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-600">Payable Amount</p>
                        <p className="text-sm font-bold text-gray-900">
                          ₹{(selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Total Paid</p>
                        <p className="text-sm font-bold text-green-700">
                          ₹{(selectedPr.total_paid_amount || 0).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Balance Amount</p>
                        <p className="text-sm font-bold text-amber-700">
                          ₹{(() => {
                            let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                            if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                              totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                            }
                            return (totalPayable - (selectedPr.total_paid_amount || 0)).toLocaleString();
                          })()}
                        </p>
                      </div>
                    </div>

                    {/* Payment History */}
                    {selectedPr.partial_payments && selectedPr.partial_payments.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-amber-900">Payment History:</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {selectedPr.partial_payments.map((payment, index) => (
                            <div key={payment.id || index} className="bg-white p-3 rounded-lg border border-amber-200">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    Payment #{index + 1}: ₹{(payment.amount || 0).toLocaleString()}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    UTR: {payment.utr_number} | {payment.payment_method}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-600">
                                    {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : 'N/A'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    by {payment.paid_by}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tax Invoice Details Section */}
                {(selectedPr.tax_invoice_date || selectedPr.credit_period || selectedPr.due_date || selectedPr.utr_number) && (
                  <div className="border-t pt-4">
                    <h3 className="text-base font-semibold text-gray-900 mb-3">Tax Invoice Details</h3>
                    <div className="bg-green-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        {selectedPr.tax_invoice_date && (
                          <div>
                            <span className="font-medium text-gray-700">Invoice Date:</span>
                            <p className="text-gray-900 mt-1">
                              {new Date(selectedPr.tax_invoice_date).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        {selectedPr.credit_period && (
                          <div>
                            <span className="font-medium text-gray-700">Credit Period:</span>
                            <p className="text-gray-900 mt-1">{selectedPr.credit_period} days</p>
                          </div>
                        )}
                        {selectedPr.due_date && (
                          <div>
                            <span className="font-medium text-gray-700">Due Date:</span>
                            <p className="text-gray-900 mt-1">
                              {new Date(selectedPr.due_date).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        {selectedPr.utr_number && (
                          <div>
                            <span className="font-medium text-gray-700">UTR Number:</span>
                            <p className="text-gray-900 mt-1">{selectedPr.utr_number}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Documents Section */}
                <div className="border-t pt-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Documents</h3>
                  
                  {(selectedPr.proforma_invoice_url || selectedPr.tax_invoice_url || selectedPr.po_url) ? (
                    <div className="space-y-3">
                      {/* Proforma Invoice */}
                      {selectedPr.proforma_invoice_url && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Proforma Invoice</p>
                            <p className="text-xs text-gray-600">Purchase order document</p>
                          </div>
                          <Button
                            onClick={() => downloadFile(
                              selectedPr.proforma_invoice_url, 
                              getFilenameWithExtension(selectedPr.proforma_invoice_url, `Proforma_Invoice_${selectedPr.id}.pdf`)
                            )}
                            variant="outline"
                            size="sm"
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      )}

                      {/* Tax Invoice */}
                      {selectedPr.tax_invoice_url && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Tax Invoice</p>
                            <p className="text-xs text-gray-600">Uploaded tax invoice</p>
                          </div>
                          <Button
                            onClick={() => downloadFile(
                              selectedPr.tax_invoice_url, 
                              getFilenameWithExtension(selectedPr.tax_invoice_url, `Tax_Invoice_${selectedPr.id}.pdf`)
                            )}
                            variant="outline"
                            size="sm"
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      )}

                      {/* Purchase Order */}
                      {selectedPr.po_url && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Purchase Order</p>
                            <p className="text-xs text-gray-600">Generated PO: {selectedPr.po_number || 'N/A'}</p>
                          </div>
                          <Button
                            onClick={() => downloadFile(selectedPr.po_url, `PO_${selectedPr.po_number || selectedPr.id}.pdf`)}
                            variant="outline"
                            size="sm"
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No documents uploaded</p>
                  )}
                </div>

                {/* Additional Info Conversation History */}
                {selectedPr.additional_info_conversation && selectedPr.additional_info_conversation.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="text-base font-semibold text-gray-900 mb-3">Additional Information History</h3>
                    <div className="bg-orange-50 rounded-lg p-4 space-y-3">
                      <div className="space-y-2">
                        {selectedPr.additional_info_conversation.map((entry, index) => (
                          <div key={index} className={`p-3 rounded ${entry.type === 'request' ? 'bg-orange-100' : 'bg-blue-100'} text-sm`}>
                            <div className="flex justify-between items-start mb-2">
                              <span className="font-medium">
                                {entry.type === 'request' ? (entry.role === 'finance' ? 'Finance' : entry.role === 'accounts' ? 'Accounts' : 'Admin') : 'Requester'}: {entry.user}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(entry.timestamp).toLocaleDateString('en-US', { 
                                  month: 'short', day: 'numeric', 
                                  hour: '2-digit', minute: '2-digit' 
                                })}
                              </span>
                            </div>
                            <p className="text-gray-900">{entry.message}</p>
                            {entry.docs && entry.docs.length > 0 && (
                              <div className="mt-1">
                                <span className="text-xs text-gray-600">Attachments: </span>
                                {entry.docs.map((doc, docIndex) => (
                                  <a 
                                    key={docIndex}
                                    href={doc} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline ml-1"
                                  >
                                    Doc {docIndex + 1}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Document Trail */}
                <div className="border-t pt-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Document Trail</h3>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Purchase Request Created</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.requested_by} on {new Date(selectedPr.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                    {selectedPr.proforma_invoice_url && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Proforma Invoice Uploaded</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.requested_by} on {new Date(selectedPr.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedPr.approved_by && selectedPr.approved_at && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Function Approved</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.approved_by} on {new Date(selectedPr.approved_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedPr.po_number && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">PO Generated</div>
                          <div className="text-xs text-gray-500">
                            PO #{selectedPr.po_number}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedPr.delivery_confirmed && selectedPr.delivery_confirmed_at && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Delivery Confirmed</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.requested_by} on {new Date(selectedPr.delivery_confirmed_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedPr.tax_invoice_url && selectedPr.tax_invoice_date && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Tax Invoice Uploaded</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.requested_by} on {new Date(selectedPr.tax_invoice_date).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedPr.accounts_approved_by && selectedPr.accounts_approved_at && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Accounts Approved</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.accounts_approved_by} on {new Date(selectedPr.accounts_approved_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedPr.finance_approved_by && selectedPr.finance_approved_at && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Finance Approved</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.finance_approved_by} on {new Date(selectedPr.finance_approved_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedPr.paid_at && (
                      <div className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">Payment Completed</div>
                          <div className="text-xs text-gray-500">
                            by {selectedPr.paid_by} on {new Date(selectedPr.paid_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Metadata */}
                <div className="border-t pt-4 text-xs text-gray-500 space-y-1">
                  {selectedPr.created_at && (
                    <p>Created: {new Date(selectedPr.created_at).toLocaleString()}</p>
                  )}
                  {selectedPr.approved_at && (
                    <p>Approved: {new Date(selectedPr.approved_at).toLocaleString()}</p>
                  )}
                  {selectedPr.finance_approved_at && (
                    <p>Finance Approved: {new Date(selectedPr.finance_approved_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
